import { Content } from '@/types/admin/who-we-are';
import { ImageInput, ImageInputProps } from '@/components/admin/image-input/ImageInput';
import { WHO_WE_ARE_TEXT } from '@/const/admin/who-we-are';
import { useMemo, useState } from 'react';
import { ImageValues } from '@/types/common/image';
import { COMMON_TEXT_ADMIN } from '@/const/admin/common';
import './ImageBlockSection.scss';
import { Button } from '@/components/admin/button/Button';
import { ContentType } from '@/types/common/about-us';
import { WHO_WE_ARE_VALIDATION_FUNCTIONS } from '@/validation/admin/who-we-are-schema/WhoWeAreSchema';
import { RichTextInputGroup } from '@/components/admin/input-groups/rich-text-input-group/RichTextInputGroup';
import { getRichTextContentFromHtml } from '@/utils/functions/rich-text-content/rich-text-content';
import { LocalizationLanguage } from '@/types/common/language';
import { DEFAULT_LOCALE } from '@/const/common/locales';
import { returnDisplayedLocalization } from '@/utils/functions/localization/localization';
import { IconButton } from '@/components/admin/icon-button/IconButton';
import { ACTION_ICONS } from '@/const/common/action-icons';
import { IMAGE_VALIDATION } from '@/const/admin/image';
import whoWeAreVariantStyles from '@/components/admin/image-input/WhoWeAreImageInput.module.scss';

export interface ImageSectionProps {
    content: Content[] | undefined;
    titleLimit?: number;
    descriptionLimit: number;
    rows?: number;
    onChange: (data: Content) => void;
    onPublish: () => void;
    onTranslate: () => void;
    imageInputProps: Omit<ImageInputProps, 'className' | 'value' | 'onChange' | 'setError'>;
    isPublishButtonActive: boolean;
    language: LocalizationLanguage;
}

export const ImageSection = ({
    content,
    titleLimit = 10,
    descriptionLimit,
    onChange,
    onPublish,
    onTranslate,
    imageInputProps,
    isPublishButtonActive,
    language,
}: ImageSectionProps) => {
    const [imageError, setImageError] = useState<string | null>(null);
    const [titleError, setTitleError] = useState<string | null>(null);
    const [descriptionError, setDescriptionError] = useState<string | null>(null);

    const imageContent = content?.find((item) => item.contentType === ContentType.Image) ?? null;
    const titleContent = content?.find((item) => item.contentType === ContentType.Title);
    const descriptionContent = content?.find((item) => item.contentType === ContentType.Description);
    const isBaseLanguage = language.code === DEFAULT_LOCALE;
    const hasImageDimensionsTooSmallError = imageError === IMAGE_VALIDATION.ImageDimensionsTooSmallError;

    const displayedTitle = useMemo(() => {
        if (!titleContent) return null;
        const titleLocalization = returnDisplayedLocalization(titleContent, language.code);
        return titleLocalization?.title ?? titleContent.title;
    }, [language.code, titleContent]);

    const displayedDescription = useMemo(() => {
        if (!descriptionContent) return null;
        const descriptionLocalization = returnDisplayedLocalization(descriptionContent, language.code);
        return descriptionLocalization?.description ?? descriptionContent.description;
    }, [language.code, descriptionContent]);

    if (!content || !descriptionContent) {
        return null;
    }

    const handleImageChange = (value: ImageValues | null) => {
        onChange({
            ...(imageContent || { contentType: ContentType.Image }),
            image: value,
            id: imageContent ? imageContent.id : 0,
            description: null,
            title: null,
            imageId: null,
            localizations: imageContent?.localizations || [],
        });
    };

    const handleTitleChange = (value: string) => {
        setTitleError(null);

        if (!titleContent || !isBaseLanguage) return;
        onChange({
            ...titleContent,
            title: value,
        });
    };

    const handleDescriptionChange = (value: string) => {
        setDescriptionError(null);

        if (!descriptionContent || !isBaseLanguage) return;
        onChange({
            ...descriptionContent,
            description: value,
        });
    };

    const handleTitleBlur = () => {
        if (!isBaseLanguage) return;
        const plainText = getRichTextContentFromHtml(displayedTitle ?? '');
        const error = WHO_WE_ARE_VALIDATION_FUNCTIONS.validateTitle(plainText);
        setTitleError(error || null);
    };

    const handleDescriptionBlur = () => {
        if (!isBaseLanguage) return;
        const plainText = getRichTextContentFromHtml(displayedDescription ?? '');
        const error = WHO_WE_ARE_VALIDATION_FUNCTIONS.validateDescription(plainText);
        setDescriptionError(error || null);
    };

    const handlePublish = () => {
        const titleError = titleContent
            ? WHO_WE_ARE_VALIDATION_FUNCTIONS.validateTitle(getRichTextContentFromHtml(displayedTitle ?? ''))
            : null;

        const descriptionError = WHO_WE_ARE_VALIDATION_FUNCTIONS.validateDescription(
            getRichTextContentFromHtml(displayedDescription ?? ''),
        );

        setTitleError(titleError || null);
        setDescriptionError(descriptionError || null);

        if (titleError || descriptionError || imageError) return;

        onPublish();
    };

    const isPublishDisabled =
        !isPublishButtonActive ||
        !!imageError ||
        !!titleError ||
        !!descriptionError ||
        !!(
            titleContent &&
            WHO_WE_ARE_VALIDATION_FUNCTIONS.validateTitle(getRichTextContentFromHtml(displayedTitle ?? ''))
        ) ||
        !!WHO_WE_ARE_VALIDATION_FUNCTIONS.validateDescription(getRichTextContentFromHtml(displayedDescription ?? ''));

    return (
        <div className="image-section">
            <div className="image-wrapper">
                <ImageInput
                    setError={setImageError}
                    value={imageContent?.image ?? null}
                    onChange={handleImageChange}
                    variant="whoWeAre"
                    label={WHO_WE_ARE_TEXT.IMAGE.INPUT}
                    disabled={!isBaseLanguage}
                    {...imageInputProps}
                    className={hasImageDimensionsTooSmallError ? whoWeAreVariantStyles['container-focused'] : undefined}
                />
                {imageError && (
                    <p data-testid="image-error" className="error">
                        {imageError}
                    </p>
                )}
            </div>

            <div className="content-wrapper">
                <div className="content-wrapper-buttons">
                    <IconButton
                        type="button"
                        className="translate-btn"
                        aria-label="Translate"
                        onClick={onTranslate}
                        DefaultIcon={ACTION_ICONS.translate.default}
                        disabled={isPublishButtonActive}
                    />
                </div>
                <div className="content-wrapper-fields">
                    {titleContent && (
                        <div className="content-wrapper-title">
                            <RichTextInputGroup
                                key={`title-${language.code}`}
                                label={COMMON_TEXT_ADMIN.TYPE.TITLE}
                                isRequired
                                value={displayedTitle ?? ''}
                                onChange={handleTitleChange}
                                name={COMMON_TEXT_ADMIN.TYPE.TITLE}
                                id={titleContent.id.toString()}
                                maxLength={titleLimit}
                                onBlur={handleTitleBlur}
                                error={isBaseLanguage ? (titleError ?? undefined) : undefined}
                                disabled={!isBaseLanguage}
                                hideToolbar={!isBaseLanguage}
                                normalizeSpaces
                                trimOnBlur
                            />
                        </div>
                    )}

                    {descriptionContent && (
                        <div className="content-wrapper-description">
                            <RichTextInputGroup
                                key={`description-${language.code}`}
                                label={COMMON_TEXT_ADMIN.TYPE.DESCRIPTION}
                                isRequired
                                onChange={handleDescriptionChange}
                                value={displayedDescription ?? ''}
                                maxLength={descriptionLimit}
                                name={COMMON_TEXT_ADMIN.TYPE.DESCRIPTION}
                                id={descriptionContent.id.toString()}
                                onBlur={handleDescriptionBlur}
                                error={isBaseLanguage ? (descriptionError ?? undefined) : undefined}
                                disabled={!isBaseLanguage}
                                hideToolbar={!isBaseLanguage}
                                normalizeSpaces
                                trimOnBlur
                            />
                        </div>
                    )}
                    {isBaseLanguage && (
                        <Button
                            className="button"
                            buttonStyle="primary"
                            onClick={handlePublish}
                            type="submit"
                            disabled={isPublishDisabled}
                        >
                            {COMMON_TEXT_ADMIN.BUTTON.SAVE_AS_PUBLISHED}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};
